'use client';

import { useEffect, useState } from 'react';
import { useRegisterServiceWorker } from '@/hooks';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import Salesforce, { SalesforceProps } from './Salesforce';
import SalesforcePzn, { SalesforcePznProps } from './SalesforcePzn';
import { deferIfPrerendering } from '@/utils/deferIfPrerendering';

interface ClientPageHeadProps {
    envName: string;
    ddenv: string;
    launchScript: string;
    onetrust: {
        script: string;
        domainScript: string;
    };
    pageInfo: {
        analyticsPageName: string;
        analyticsPageType: string;
        pageType: PageType;
    };
    abTest?: SalesforceProps;
    pznAbTest?: SalesforcePznProps;
}

export default function ClientPageHead(props: ClientPageHeadProps): React.JSX.Element {
    const [isAnalyticsReady, setIsAnalyticsReady] = useState<boolean>(false);
    const [ddLoaded, setDdLoaded] = useState<boolean>(false);
    const searchParams = useSearchParams();

    useRegisterServiceWorker('/service-worker.js');

    useEffect(() => {
        window.dispatchEvent(new Event('hydrate'));
    }, []);

    useEffect(() => {
        const paramStr = searchParams.toString();
        try {
            if (paramStr) {
                window.sessionStorage.setItem('paramStr', paramStr);
            } else {
                window.sessionStorage.removeItem('paramStr');
            }
        } catch (e) {
            console.error(e);
        }
    }, [searchParams]);

    useEffect(() => {
        const browserWindow = window as BrowserWindow;

        if (browserWindow.DD_RUM && process.env.NODE_ENV === 'production') {
            browserWindow.DD_RUM.init({
                clientToken: 'pub2efebd86de973d3161e20023ac07e68a',
                applicationId: 'c27edd08-fd30-4d05-ace8-21ad46980f08',
                site: 'datadoghq.com',
                service: 'sxm-cecm-web-www',
                env: props.ddenv,
                version: process.env.NEXT_PUBLIC_DD_VERSION || '1.0.0',
                sessionSampleRate: 45,
                sessionReplaySampleRate: 20,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: 'mask-user-input',
            });

            browserWindow.DD_RUM.startSessionReplayRecording();
            browserWindow.DD_RUM.setGlobalContext(props.pageInfo);
        }
    }, [ddLoaded, props.ddenv, props.pageInfo]);

    return (
        <>
            <Script
                src="https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js"
                onLoad={() => {
                    setDdLoaded(true);
                }}
            />

            <Script
                src={props.launchScript}
                async
                strategy="afterInteractive"
                onLoad={() => {
                    const script = document.createElement('script');
                    script.setAttribute('src', '/resources/js/analytics.js');
                    script.onload = () => setIsAnalyticsReady(true);
                    document.body.appendChild(script);
                }}
            />

            {process.env.NODE_ENV !== 'development' && (
                <Script
                    src={props.onetrust.script}
                    strategy="afterInteractive"
                    data-domain-script={props.onetrust.domainScript}
                    onLoad={() => {
                        const browserWindow = window as BrowserWindow;
                        browserWindow.OptanonWrapper = () => {
                            const activeGroups = browserWindow.OnetrustActiveGroups ?? '';

                            browserWindow.DD_RUM?.setGlobalContextProperty('consent', activeGroups);
                            window.dispatchEvent(
                                new CustomEvent('oneTrustConsentChanged', {
                                    detail: { activeGroups },
                                }),
                            );
                            if (!activeGroups.includes('C0004')) {
                                const clearPersonalizationCookies = () => {
                                    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.siriusxm.com;';
                                    const personalizationCookies = ['sxm_offer_seg', 'sxm_user_info', 'sxm_content_seg'];
                                    personalizationCookies.forEach(
                                        (cookie) => (document.cookie = `${cookie}=; ${expired}`),
                                    );
                                };

                                // defer cookie deletion during prerender - these cookies are scoped to
                                // .siriusxm.com so deleting them early would affect care.siriusxm.com
                                // and other subdomains before the user has actually landed on the page
                                deferIfPrerendering(clearPersonalizationCookies);
                            }
                        };
                    }}
                />
            )}
            {isAnalyticsReady && props.abTest && <Salesforce {...props.abTest} />}
            {isAnalyticsReady && props.pznAbTest && <SalesforcePzn {...props.pznAbTest} />}
        </>
    );
}
